import { useEffect, useState } from "react";
import { Button, Card, CardContent, CardHeader, EmptyState, Flex, Heading, Icon, Icons, Paragraph, useToast } from "@ir/ui";
import { DeductionActivityCard } from "./DeductionActivityCard";
import { Deduction, DeductionActivity } from "@/core/entities/deduction";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { DeductionActivityRepository } from "@/infrastructure/repositories/deduction";
import { GetDeductionActivityUseCase } from "@/core/usecases/deduction";
import { DeductionActivitySortOrder, IExportDeductionActivityRepositoryRequest } from "@/core/repositories/deduction";
import { DeductionActivitySkeleton } from "./DeductionActivitySkeleton";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import { WorkspacePermissions } from "@/core/permissions/Workspace.permissions";
import { HasAnyPermission } from "@/presentation/workspace";
import { useWorkspace } from "@/hooks";
import { ExportDeductionActivityUseCase } from "@/core/usecases/deduction/ExportDeductionActivityUseCase";
import { AppConfig } from "@/infrastructure/services/config";
import { createLogger } from "@/infrastructure/services/logger";

const _appConfig = AppConfig.getInstance();

const TIME_ZONE = _appConfig.TIMEZONE;
const PAGINATION_PAGE_LIMIT = 20;
const SCROLL_THRESHOLD = 30;

// GraphQL client
const client = GraphQlAdapter.getInstance().getClient();

interface DeductionActivitiesProps {
  deduction: Deduction;
  scrollRef: React.RefObject<HTMLDivElement>;
}

export const DeductionActivities = ({ deduction, scrollRef }: DeductionActivitiesProps) => {
  const [deductionActivities, setDeductionActivities] = useState<DeductionActivity[]>([]);
  const [isDeductionActivitiesLoading, setIsDeductionActivitiesLoading] = useState<boolean>(false);
  const [isDeductionActivitiesError, setIsDeductionActivitiesError] = useState<boolean>(false);
  const [isDeductionActivitiesLoadingMore, setIsDeductionActivitiesLoadingMore] = useState<boolean>(false);

  const [sortOrder, setSortOrder] = useState<DeductionActivitySortOrder>(DeductionActivitySortOrder.DESCEND);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isDeductionActivitiesExporting, setIsDeductionActivitiesExporting] = useState<boolean>(false);
  const { settings: workspaceSettings } = useWorkspace();

  const { setDeductionsLoading, drawerInfo } = useDeductionDrawer();
  const { error: errorToast } = useToast();
  const logger = createLogger("DeductionActivities");

  useEffect(() => {
    const handleScroll = async () => {
      if (!scrollRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom <= SCROLL_THRESHOLD;

      if (isNearBottom && !isDeductionActivitiesLoading && isLoadingMore) {
        await getDeductionActivities();
      }
    };

    const currentScrollRef = scrollRef.current;
    if (currentScrollRef) {
      currentScrollRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (currentScrollRef) {
        currentScrollRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, [scrollRef, isLoadingMore, currentPage, totalCount, isDeductionActivitiesLoading]);

  const getDeductionActivities = async () => {
    if (!deduction?.id) return;

    setDeductionsLoading(true);

    if (currentPage === 1) {
      setIsDeductionActivitiesLoading(true);
    } else {
      setIsDeductionActivitiesLoadingMore(true);
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }

    try {
      const deductionActivityRepository = new DeductionActivityRepository(client);
      const deductionActivityResponse = await new GetDeductionActivityUseCase(deductionActivityRepository).execute({
        deduction_id: deduction.id,
        page_number: currentPage,
        page_size: PAGINATION_PAGE_LIMIT,
        sort_order: sortOrder,
      });

      setIsLoadingMore(deductionActivityResponse?.hasMore ?? false);
      if (currentPage === 1) {
        setDeductionActivities(deductionActivityResponse?.deductionActivities ?? []);
      } else {
        setDeductionActivities(prev => {
          const newDeductionActivities = deductionActivityResponse?.deductionActivities ?? [];
          const existingIds = new Set(prev?.map(activity => activity.id));
          const filteredNewActivities = newDeductionActivities.filter(activity => !existingIds.has(activity.id));
          return [...prev, ...filteredNewActivities];
        });
      }

      if (deductionActivityResponse?.deductionActivities?.length !== 0) {
        setCurrentPage(currentPage + 1);
      }
      setTotalCount(deductionActivityResponse?.total);
      setIsDeductionActivitiesLoading(false);
      setIsDeductionActivitiesLoadingMore(false);
      setDeductionsLoading(false);
    } catch (error) {
      logger.error("Error getting deduction activities", error);
      setIsDeductionActivitiesError(true);
      setIsDeductionActivitiesLoading(false);
      setIsDeductionActivitiesLoadingMore(false);
      setDeductionsLoading(false);
    } finally {
      setIsDeductionActivitiesLoading(false);
      setIsDeductionActivitiesLoadingMore(false);
      setDeductionsLoading(false);
    }
  };

  useEffect(() => {
    if (isDeductionActivitiesLoadingMore && scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [isDeductionActivitiesLoadingMore]);

  useEffect(() => {
    getDeductionActivities();
  }, [sortOrder]);

  useEffect(() => {
    setDeductionActivities([]);
    getDeductionActivities();

    return () => {
      setDeductionActivities([]);
      setIsDeductionActivitiesLoading(false);
      setIsDeductionActivitiesLoadingMore(false);
      setIsDeductionActivitiesError(false);
    };
  }, [deduction]);

  const handleSortOrder = async () => {
    setCurrentPage(1);
    setSortOrder(sortOrder === DeductionActivitySortOrder.DESCEND ? DeductionActivitySortOrder.ASCEND : DeductionActivitySortOrder.DESCEND);
  };

  const handleExport = async () => {
    setIsDeductionActivitiesExporting(true);
    try {
      const exportDeductionActivityRepositoryRequest: IExportDeductionActivityRepositoryRequest = {
        params: {
          deduction_id: deduction.id,
          sort_order: sortOrder.toLowerCase(),
          invoice_number: drawerInfo?.invoiceNumber ?? "",
          total_amount: +(deduction?.totalAmount ?? 0),
          time_zone: TIME_ZONE,
        },
        formats: {
          date_time: workspaceSettings?.displayDateFormatWithTime ?? "",
        },
      };

      const deductionActivityRepository = new DeductionActivityRepository(client);
      const response = await new ExportDeductionActivityUseCase(deductionActivityRepository).execute(exportDeductionActivityRepositoryRequest);
      if (response?.success) {
        globalThis.open(response.signedUrl, "_blank");
      } else {
        errorToast({
          content: "Failed to export deduction activity",
        });
      }
    } catch (error) {
      logger.error("Error exporting deduction activity", error);
      errorToast({
        content: "Failed to export deduction activity",
      });
    } finally {
      setIsDeductionActivitiesExporting(false);
    }
  };

  return (
    <Card className="bg-white">
      <CardHeader className="px-4 pt-4 pb-0">
        <Flex justifyContent="end" align="center">
          {deductionActivities.length > 0 && (
            <Button isDisabled={isDeductionActivitiesLoading} variant="clear" color="grey" size="medium" onPress={handleSortOrder}>
              <Icon size="medium" slot="start" name={sortOrder === DeductionActivitySortOrder.DESCEND ? Icons.SwitchVertical02 : Icons.SwitchVertical01} />
              {sortOrder === DeductionActivitySortOrder.DESCEND ? "Newest first" : "Oldest first"}
            </Button>
          )}
          {deductionActivities.length > 0 && (
            <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTION_ACTIVITY_EXPORT]}>
              <Button
                isDisabled={isDeductionActivitiesLoading || isDeductionActivitiesExporting}
                variant="outline"
                color="grey"
                size="medium"
                onPress={handleExport}
              >
                <Icon
                  name={isDeductionActivitiesExporting ? Icons.Loading : Icons.Download02}
                  size="medium"
                  slot="start"
                  className={isDeductionActivitiesExporting ? "icon-fast-loading" : ""}
                />
                Export
              </Button>
            </HasAnyPermission>
          )}
        </Flex>
      </CardHeader>

      <CardContent className="px-4">
        {isDeductionActivitiesLoading && currentPage === 1 && <DeductionActivitySkeleton count={8} />}
        {deductionActivities.length === 0 && !isDeductionActivitiesLoading && !isDeductionActivitiesError && (
          <EmptyState
            className="bg-white border-1 border-color-grey-25 py-5"
            icon={Icons.BarChart5}
            title={
              <Heading level={6} className="text-grey-700 font-bold mt-3">
                No activity found
              </Heading>
            }
          />
        )}
        {!isDeductionActivitiesLoading && (
          <Flex direction="column" style={{ width: "100%" }} align="stretch" justifyContent="end" className="mb-2">
            {deductionActivities.map((activity: DeductionActivity) => (
              <DeductionActivityCard key={activity.id} activity={activity} />
            ))}
          </Flex>
        )}
        {isDeductionActivitiesLoadingMore && <DeductionActivitySkeleton count={3} />}
        {isDeductionActivitiesError && !isDeductionActivitiesLoading && (
          <EmptyState
            className="bg-white border-1 border-color-grey-25 py-3 flex-1"
            icon={Icons.AlertCircle}
            title={
              <Heading level={6} className="text-grey-700 font-bold mt-3">
                Something went wrong
              </Heading>
            }
            description={<Paragraph className="text-grey-500 font-regular mb-0">Failed to load deduction activity</Paragraph>}
          />
        )}
      </CardContent>
    </Card>
  );
};
